import apiClient from '@/scripts/api/apiClient';

/**
 * Channel API endpoints
 */

// Query key factories
export const channelKeys = {
  getAllChannels: () => ['get', 'channels'],
  getChannel: (channelId) => ['get', 'channels', channelId],
  updateChannel: (channelId) => ['update', 'channels', channelId],
  deleteChannel: (channelId) => ['delete', 'channels', channelId],
  getChannelVideos: (channelId) => ['get', 'channels', channelId, 'videos'],
  createChannelVideo: (channelId) => ['create', 'channels', channelId, 'videos'],
  getChannelVideoFiles: (channelId) => ['get', 'channels', channelId, 'videoFiles'],
  createChannelVideoFile: (channelId) => ['create', 'channels', channelId, 'videoFiles'],
};

/**
 * Get all channels
 * @returns {Promise<Array>} List of channels
 */
export const getAllChannels = async () => {
  const response = await apiClient.get('/api/channels');
  return response.data;
};

/**
 * Get a specific channel by ID
 * @param {string} channelId - Channel ID
 * @returns {Promise<Object>} Channel details
 */
export const getChannel = async (channelId) => {
  const response = await apiClient.get(`/api/channels/${channelId}`);
  return response.data;
};

/**
 * Create a new channel for an organization
 * @param {string} organizationId - Organization ID
 * @param {Object} channelData - Channel data
 * @returns {Promise<Object>} Created channel
 */
export const createChannel = async (organizationId, channelData) => {
  const response = await apiClient.post(`/api/organizations/${organizationId}/channels`, channelData);
  return response.data;
};

/**
 * Update a channel
 * @param {string} channelId - Channel ID
 * @param {Object} channelData - Channel data to update
 * @returns {Promise<Object>} Updated channel
 */
export const updateChannel = async (channelId, channelData) => {
  const response = await apiClient.put(`/api/channels/${channelId}`, channelData);
  return response.data;
};

/**
 * Delete a channel
 * @param {string} channelId - Channel ID
 * @returns {Promise<Object>} Response data
 */
export const deleteChannel = async (channelId) => {
  const response = await apiClient.delete(`/api/channels/${channelId}`);
  return response.data;
};

/**
 * Get videos for a specific channel
 * @param {string} channelId - Channel ID
 * @returns {Promise<Array>} List of videos in the channel
 */
export const getChannelVideos = async (channelId) => {
  const response = await apiClient.get(`/api/channels/${channelId}/videos`);
  return response.data;
};

/**
 * Create a video for a channel
 * @param {string} channelId - Channel ID
 * @param {Object} videoData - Video data
 * @returns {Promise<Object>} Created video
 */
export const createChannelVideo = async (channelId, videoData) => {
  const response = await apiClient.post(`/api/channels/${channelId}/videos`, videoData);
  return response.data;
};

/**
 * Get video files for a specific channel
 * @param {string} channelId - Channel ID
 * @returns {Promise<Array>} List of video files in the channel
 */
export const getChannelVideoFiles = async (channelId) => {
  const response = await apiClient.get(`/api/channels/${channelId}/video-files`);
  return response.data;
};

/**
 * Create a video file for a channel
 * @param {string} channelId - Channel ID
 * @param {Object} videoFileData - Video file data
 * @returns {Promise<Object>} Created video file
 */
export const createChannelVideoFile = async (channelId, videoFileData) => {
  const response = await apiClient.post(`/api/channels/${channelId}/video-files`, videoFileData);
  return response.data;
};